/**
 * Session Management Utility
 * Handles persistent login with 3-4 day session expiry
 */

interface SessionData {
  access: string;
  refresh: string;
  user: any;
  loginTime: number;
  expiryTime: number;
}

const SESSION_KEY = 'adminSession';
const SESSION_DURATION = 4 * 24 * 60 * 60 * 1000; // 4 days in milliseconds

export class SessionManager {
  /**
   * Save session data to localStorage
   */
  static saveSession(token: string, refreshToken: string, user: any): void {
    const now = Date.now();
    const sessionData: SessionData = {
      access: token,
      refresh: refreshToken,
      user,
      loginTime: now,
      expiryTime: now + SESSION_DURATION
    };
    
    localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
  }

  /**
   * Get current session data
   */
  static getSession(): SessionData | null {
    try {
      const sessionStr = localStorage.getItem(SESSION_KEY);
      if (!sessionStr) return null;

      const sessionData: any = JSON.parse(sessionStr);
      
      // Check if session is expired
      if (Date.now() > sessionData.expiryTime) {
        this.clearSession();
        return null;
      }

      // MIGRATION: Handle old session format (token -> access)
      if (sessionData.token && !sessionData.access) {
        console.log('🔄 Migrating old session format...');
        sessionData.access = sessionData.token;
        delete sessionData.token;
        localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
      }

      // MIGRATION: Handle old refreshToken -> refresh
      if (sessionData.refreshToken && !sessionData.refresh) {
        sessionData.refresh = sessionData.refreshToken;
        delete sessionData.refreshToken;
        localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
      }

      return sessionData as SessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      this.clearSession();
      return null;
    }
  }

  /**
   * Check if user is currently logged in
   */
  static isLoggedIn(): boolean {
    const session = this.getSession();
    return session !== null;
  }

  /**
   * Get current access token
   */
  static getAccessToken(): string | null {
    const session = this.getSession();
    return session?.access || null;
  }

  /**
   * Get current refresh token
   */
  static getRefreshToken(): string | null {
    const session = this.getSession();
    return session?.refresh || null;
  }

  /**
   * Get current user data
   */
  static getCurrentUser(): any | null {
    const session = this.getSession();
    return session?.user || null;
  }

  /**
   * Update session with new token (after refresh)
   */
  static updateToken(newToken: string): void {
    const session = this.getSession();
    if (session) {
      session.access = newToken;
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    }
  }

  /**
   * Clear session data
   */
  static clearSession(): void {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRefreshToken');
    localStorage.removeItem('adminUser');
  }

  /**
   * Get time remaining until session expires (in milliseconds)
   */
  static getTimeUntilExpiry(): number {
    const session = this.getSession();
    if (!session) return 0;
    
    return Math.max(0, session.expiryTime - Date.now());
  }

  /**
   * Check if session will expire soon (within 1 hour)
   */
  static isExpiringSoon(): boolean {
    const timeUntilExpiry = this.getTimeUntilExpiry();
    const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
    return timeUntilExpiry < oneHour && timeUntilExpiry > 0;
  }
}
